<template>
  <div class="folio-modal-wrapper">
    <v-dialog
      v-model="dialogOpen"
      :max-width="$vuetify.breakpoint.mobile ? '100%' : 1100"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
      :retain-focus="false"
      content-class="folio-dialog"
    >
      <!-- Loading state -->
      <v-card v-if="currentFolio && currentFolio.loading" class="modal-card loading-card">
        <div class="loading-header">
          <div class="modal-brand">
            <span class="brand-icon-dark"><v-icon small color="#2D4B95">mdi-book-open-page-variant-outline</v-icon></span>
            <span class="brand-label-dark">Cargando Folio</span>
          </div>
          <button class="close-btn-dark" @click="closeModal">
            <v-icon size="18" color="#555">mdi-close</v-icon>
          </button>
        </div>
        <div class="loading-body">
          <v-progress-circular :size="40" :width="3" color="#3B5FDB" indeterminate />
          <p class="loading-text">Cargando información…</p>
        </div>
      </v-card>

      <!-- Main card -->
      <v-card v-else-if="currentFolio" class="modal-card">

        <ModalStickyHeader :breakpoint="600">
        <!-- ── TOP BAR ── -->
        <template #topbar>
        <div class="topbar">
          <div class="topbar-left">
            <span class="topbar-icon"><v-icon small color="#2D4B95">mdi-book-open-page-variant-outline</v-icon></span>
            <div class="topbar-titles">
              <p class="mb-0 caption">Folio <span class="caption font-weight-bold topbar-number">{{ currentFolio.code }}</span></p>
              <h2 class="topbar-title">{{ currentFolio.title }}</h2>
            </div>
          </div>
          <div class="topbar-right">
            <v-menu v-if="canManageCommunicationBooks && !isOpeningFolio" v-model="metadataMenuOpen" offset-y :close-on-content-click="false" min-width="300" max-width="300">
              <template v-slot:activator="{ on: onMenu }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: onTooltip }">
                    <v-btn
                      class="topbar-action-btn"
                      dark
                      x-small
                      color="#5E85EF"
                      :disabled="currentFolio.status !== 'published' || metadataSaving"
                      v-on="{ ...onMenu, ...onTooltip }"
                    >
                      <v-icon dark small>mdi-clipboard-edit-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Modificar Folio</span>
                </v-tooltip>
              </template>
              <div class="metadata-menu">
                <div class="bg-light-blue-text py-1 px-2">
                  <p class="caption mb-0 white--text font-weight-bold">Modificar Folio</p>
                </div>
                <div class="pa-2" v-if="currentFolio.status === 'published'">
                  <p class="mb-0 caption light-blue-text font-weight-bold">Cambiar Prioridad</p>
                  <div class="d-flex flex-row">
                    <v-btn
                      v-for="option in editablePriorityOptions"
                      :key="option.value"
                      class="mx-2 my-1"
                      small
                      dark
                      :color="priorityButtonColor(option.value)"
                      :loading="metadataSaving"
                      @click="updatePriority(option.value)"
                    >
                      <p class="mb-0">! {{ option.text }}</p>
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-menu>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="topbar-action-btn" dark x-small color="#2D4B95" :loading="loadingPdf" v-bind="attrs" v-on="on" @click="downloadPdf">
                  <v-icon dark small>mdi-file-pdf-box</v-icon>
                </v-btn>
              </template>
              <span>Descargar en PDF</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="topbar-action-btn" dark x-small color="#2D4B95"
                  :class="{ 'topbar-action-btn--disabled': !attachments.length }"
                  :loading="loadingFilePath" :disabled="!attachments.length"
                  v-bind="attrs" v-on="on" @click="downloadAttachments">
                  <v-icon dark small>mdi-download</v-icon>
                </v-btn>
              </template>
              <span>Descargar Adjuntos</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="topbar-action-btn" dark x-small color="#fb8c00"
                  v-bind="attrs" v-on="on" @click="minimizeFolio">
                  <v-icon dark small>mdi-window-minimize</v-icon>
                </v-btn>
              </template>
              <span>Minimizar</span>
            </v-tooltip>
            <v-btn class="topbar-action-btn big" dark small color="#F4516C" @click="closeModal">
              <v-icon dark small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        </template>

        <!-- ── STATUS STRIP ── -->
        <template #status-strip>
        <div class="status-strip">
          <div class="status-strip-pills">
            <span class="pill pill--blue">
              <v-icon size="12" color="#2D4B95">mdi-calendar-month-outline</v-icon>
              {{ formatDate(currentFolio.published_at) }}
            </span>
            <span class="pill pill--green" v-if="currentFolio.status === 'finished'">
              <v-icon size="12" color="#1B7A4E">mdi-check-circle-outline</v-icon>
              Finalizado
            </span>
            <span class="pill pill--blue" v-else>
              <v-icon size="12" color="#2D4B95">mdi-book-open-page-variant-outline</v-icon>
              Publicado
            </span>
            <span class="pill" :class="priorityClass">
              <v-icon size="12">mdi-flag-outline</v-icon>
              Prioridad: {{ priorityLabel }}
            </span>
          </div>
          <div class="status-strip-actions">
            <v-btn
              v-if="canSign"
              color="#1B7A4E"
              dark
              small
              depressed
              :loading="signatureUploading || isSigning"
              @click="handleSignFolio"
            >
              <v-icon left small>mdi-draw-pen</v-icon>
              Firmar Folio
            </v-btn>
            <v-btn v-if="currentFolio.status === 'published'" color="#2D4B95" dark small depressed :loading="finishing" :disabled="!canFinish" :title="finishDisabledReason" @click="finish">
              <v-icon left small>mdi-check-circle-outline</v-icon>
              Finalizar Folio
            </v-btn>
            <div class="task-closed-notice" v-else>
              <v-icon size="18" color="#1B7A4E">mdi-lock-check-outline</v-icon>
              <span>Este folio está finalizado.</span>
            </div>
          </div>
        </div>
        </template>
        </ModalStickyHeader>

        <!-- ── TWO COLUMN BODY ── -->
        <div class="modal-body">

          <!-- LEFT: detail panel -->
          <div class="panel-detail">
            <div class="panel-scroll">

              <!-- Meta grid -->
              <section class="meta-section">
                <div class="meta-two-columns">
                  <div class="meta-left-column">
                    <div class="meta-item">
                      <span class="meta-label">Libro</span>
                      <span class="meta-value">{{ bookName }}</span>
                    </div>
                  </div>
                  <div class="meta-right-column">
                    <div class="meta-item">
                      <span class="meta-label">Publicado por</span>
                      <span class="meta-value">{{ publisherName }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Responsable</span>
                      <div class="meta-value meta-responsible">
                        <span>{{ responsibleName }}</span>
                        <v-menu
                          v-if="canManageCommunicationBooks && !isOpeningFolio"
                          offset-y
                          @input="checkUsers"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <button class="icon-action-btn" v-bind="attrs" v-on="on" :disabled="currentFolio.status !== 'published' || metadataSaving">
                              <v-icon size="14" color="#2D4B95">mdi-swap-horizontal-bold</v-icon>
                            </button>
                          </template>
                          <v-list dense class="py-0 responsible-menu">
                            <v-list-item dense style="min-height:32px; background:#2D4B95;">
                              <p class="caption mb-0 white--text font-weight-bold">Cambiar responsable</p>
                            </v-list-item>
                            <div class="search-responsible-wrapper" @click.stop>
                              <v-icon size="14" color="#999">mdi-magnify</v-icon>
                              <input
                                v-model="searchUserResponsable"
                                type="text"
                                placeholder="Buscar usuario…"
                                class="search-responsible-input"
                                @click.stop
                              />
                            </div>
                            <div v-if="$store.usersStore.state.loadingUserListForItems" class="pa-3 text-center">
                              <v-progress-linear indeterminate color="#2D4B95" />
                            </div>
                            <v-list-item-group v-else color="#2D4B95" class="overflow-y-auto" style="max-height:260px">
                              <v-list-item
                                v-for="user in filteredResponsibleUsers"
                                :key="user.id"
                                dense
                                class="px-0 py-0"
                                @click="updateResponsable(user.id)"
                              >
                                <div style="width:100%">
                                  <div class="d-flex align-center px-3 py-2 gap-3">
                                    <img v-if="user.info && user.info.avatar" :src="user.info.avatar" class="user-avatar-sm" />
                                    <v-icon v-else size="20">mdi-account</v-icon>
                                    <div>
                                      <p class="mb-0 caption font-weight-bold" style="color:#333">{{ user.name }}<span v-if="user.projectjob && user.projectjob.userjobtype"> — <span style="color:#2D4B95">{{ user.projectjob.userjobtype.name }}</span></span></p>
                                      <p class="mb-0 caption" style="color:#777">{{ user.email }}</p>
                                    </div>
                                  </div>
                                  <v-divider />
                                </div>
                              </v-list-item>
                            </v-list-item-group>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section v-if="canManageCommunicationBooks" class="content-section visibility-section">
                <h3 class="section-title">Visibilidad</h3>
                <v-select
                  :value="currentFolio.visibility || 'restricted'"
                  :items="visibilityOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  :loading="visibilitySaving"
                  :disabled="visibilitySaving"
                  @change="requestVisibilityChange"
                />
                <p v-if="currentFolio.visibility === 'public'" class="visibility-warning">
                  Este Folio puede ser consultado por cualquier persona que tenga su código QR o token de verificación.
                </p>
                <p v-if="visibilityError" class="visibility-error">
                  No fue posible cambiar la visibilidad del Folio.
                </p>
              </section>

              <!-- Description -->
              <section class="content-section">
                <div class="section-heading-row">
                  <h3 class="section-title">Descripción</h3>
                  <v-btn
                    v-if="canEditDescription && !descriptionEditing"
                    icon
                    small
                    title="Editar descripción"
                    @click="startDescriptionEdit"
                  >
                    <v-icon small color="#2D4B95">mdi-pencil-outline</v-icon>
                  </v-btn>
                </div>
                <div v-if="!descriptionEditing" class="description-body" v-html="currentFolio.description"></div>
                <div v-else>
                  <InteractionEditor
                    v-model="descriptionDraft"
                    item_type="communication_folio"
                    :item="currentFolio"
                    :allow-inline-images="false"
                  />
                  <p class="description-warning">
                    Guardar crea una nueva interacción e invalida las firmas y tomas de conocimiento vigentes.
                  </p>
                  <p v-if="descriptionError" class="description-error">No fue posible actualizar la descripción.</p>
                  <div class="description-actions">
                    <v-btn text small :disabled="descriptionSaving" @click="cancelDescriptionEdit">Cancelar</v-btn>
                    <v-btn color="#2D4B95" dark small :loading="descriptionSaving" @click="saveDescription">Guardar descripción</v-btn>
                  </div>
                </div>
              </section>

              <section v-if="!signatures.length" class="content-section signature-section">
                <div class="acknowledgement-heading">
                  <h3 class="section-title">Firmas</h3>
                </div>
                <p class="signature-empty">Este Folio aún no registra firmas.</p>
              </section>

              <section v-if="currentFolio.verification_code && currentFolio.verification_code.qr_code_url" class="content-section verification-section">
                <h3 class="section-title">Verificación pública</h3>
                <div class="verification-card">
                  <img :src="currentFolio.verification_code.qr_code_url" alt="Código QR de verificación" class="verification-qr" />
                  <div>
                    <p class="verification-label">Código</p>
                    <p class="verification-code">{{ currentFolio.verification_code.code }}</p>
                    <p class="verification-hint">Escanea este código para consultar el resumen público.</p>
                  </div>
                </div>
              </section>

              <section class="content-section acknowledgement-section">
                <div class="acknowledgement-heading">
                  <h3 class="section-title">Toma de conocimiento</h3>
                  <SeenByUsers
                    v-if="acknowledgements.length"
                    v-model="acknowledgementMenuOpen"
                    :seen-by-users="acknowledgements"
                    label="Tomaron conocimiento"
                  />
                </div>
                <div v-if="acknowledgedByMe" class="acknowledgement-confirmed">
                  <v-icon size="17" color="#1B7A4E">mdi-check-circle-outline</v-icon>
                  <span>Has tomado conocimiento de este Folio.</span>
                </div>
                <button
                  v-else-if="currentFolio.status === 'published'"
                  class="acknowledge-btn"
                  :disabled="acknowledgementLoading"
                  @click="acknowledge"
                >
                  <v-progress-circular v-if="acknowledgementLoading" :size="14" :width="2" indeterminate color="white" />
                  <v-icon v-else size="15" color="white">mdi-check-circle-outline</v-icon>
                  Tomar conocimiento
                </button>
                <p v-if="acknowledgementError" class="acknowledgement-error">
                  No fue posible registrar la toma de conocimiento.
                </p>
              </section>

              <!-- Attachments -->
              <section class="content-section" v-if="attachments.length">
                <h3 class="section-title">Archivos</h3>
                <div class="attachments-list">
                  <div class="attachment-row" v-for="(file, i) in attachments" :key="file.id || i">
                    <div class="attachment-info">
                      <v-icon size="16" color="#3B5FDB">mdi-file-outline</v-icon>
                      <span class="attachment-name">{{ file.name || file.original_name || 'Archivo' }}</span>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Associations -->
              <section class="content-section" v-if="associations.length">
                <h3 class="section-title">Asociaciones</h3>
                <div class="attachments-list">
                  <v-chip v-for="association in associations" :key="association.id || association.key" small outlined>
                    <v-icon left x-small>mdi-link-variant</v-icon>
                    {{ association.name || association.title || association.type || 'Asociación' }}
                  </v-chip>
                </div>
              </section>

              <ClosureSignature
                v-if="signatures.length"
                :item="currentFolio"
                item-type="communication_folio"
                :item-label="currentFolio.code || currentFolio.title"
              />

              <section v-if="signatureHistory.length" class="content-section signature-history-section">
                <h3 class="section-title">Historial de firmas</h3>
                <div v-for="signature in signatureHistory" :key="signature.id" class="signature-history-row" :class="{ 'signature-history-row--invalid': signature.invalidated_at }">
                  <v-icon size="16" :color="signature.invalidated_at ? '#C62828' : '#1B7A4E'">
                    {{ signature.invalidated_at ? 'mdi-close-circle-outline' : 'mdi-check-circle-outline' }}
                  </v-icon>
                  <div>
                    <strong>{{ signature.user_name }}</strong>
                    <div class="caption">{{ signature.job_type || 'Firma digital' }} · {{ formatDateTime(signature.signed_at) }}</div>
                    <div v-if="signature.invalidated_at" class="signature-invalid-label">Invalidada por nueva interacción · {{ formatDateTime(signature.invalidated_at) }}</div>
                  </div>
                </div>
              </section>

            </div>
          </div>

          <!-- RIGHT: interactions panel -->
          <div class="panel-interactions">
            <div class="interactions-header">
              <h3 class="section-title" style="margin:0">Interacción</h3>
            </div>

            <div class="interactions-feed" v-if="timelineItems.length">
              <Interactions
                item-type="communication_folio_interactions"
                :interactions="timelineItems"
                :item="currentFolio"
                @reload="reloadFolio"
                @reply-interaction="setReplyInteraction"
              />
            </div>
            <div class="empty-interactions" v-else>
              <v-icon size="36" color="#ddd">mdi-chat-outline</v-icon>
              <p>Sin interacciones aún</p>
            </div>

            <div class="compose-area" v-if="canInteract">
              <div class="reply-banner" v-if="replyInteraction">
                <div class="d-flex align-center gap-2">
                  <v-icon size="14" color="#2D4B95">mdi-reply</v-icon>
                  <span class="caption font-weight-bold" style="color:#2D4B95">
                    Respondiendo a {{ replyInteraction.user ? replyInteraction.user.name : 'una interacción' }}
                  </span>
                </div>
                <button class="icon-action-btn" @click="replyInteraction = null">
                  <v-icon size="13">mdi-close</v-icon>
                </button>
              </div>

              <InteractionEditor
                v-model="interactionMessage"
                item_type="communication_folio"
                :item="currentFolio"
                :allow-inline-images="false"
                @mentions-updated="mentionedUserIds = $event"
              />

              <div v-if="filesPreview.length" class="compose-attachments mt-2">
                <div v-for="(file, index) in filesPreview" :key="file.uuid || file.id || index" class="compose-attachment">
                  <v-icon size="15" color="#2D4B95">mdi-file-outline</v-icon>
                  <span>{{ file.name }}</span>
                  <span v-if="file.source === 'library'" class="attachment-reference-tag">Biblioteca</span>
                  <button type="button" class="icon-action-btn" @click="removeAttachment(file)">
                    <v-icon size="13">mdi-close</v-icon>
                  </button>
                </div>
              </div>

              <div class="compose-actions mt-2">
                <button class="send-btn" :disabled="sendingInteraction" @click="sendInteraction">
                  <v-progress-circular v-if="sendingInteraction" :size="14" :width="2" indeterminate color="white" />
                  <v-icon v-else size="15" color="white">mdi-send</v-icon>
                  Enviar interacción
                </button>
                <AttachmentSourceButton
                  :disabled="sendingInteraction"
                  @files-selected="onFilesSelected"
                  @library-file-selected="onLibraryFileSelected"
                />
              </div>
            </div>
          </div>

        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="visibilityDialog" max-width="470">
      <v-card>
        <v-card-title>Hacer público el Folio</v-card-title>
        <v-card-text>
          Cualquier persona que tenga el código QR o token podrá consultar el contenido y los elementos relacionados de este Folio.
          Podrás restringirlo nuevamente después.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelVisibilityChange">Cancelar</v-btn>
          <v-btn color="#2D4B95" dark @click="confirmVisibilityChange">Hacer público</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SignaturePadModal :visible="showSignaturePad" @save="onSignatureSaved" @close="showSignaturePad = false" />
    <PinEntryModal :visible="showPinModal" :submitting="isSigning" @submit="onPinSubmit" @resend="requestFolioPin" @close="showPinModal = false" />
  </div>
</template>

<script>
import InteractionEditor from '@/components/Globals/Helpers/InteractionEditor'
import Interactions from '@/components/Globals/Helpers/Interactions'
import AttachmentSourceButton from '@/components/Globals/Helpers/AttachmentSourceButton'
import SeenByUsers from '@/components/Globals/Helpers/SeenByUsers'
import ClosureSignature from '@/components/Globals/Helpers/ClosureSignature'
import ModalStickyHeader from '@/components/Globals/Helpers/ModalStickyHeader'
import SignaturePadModal from '@/components/Globals/Modals/Signing/SignaturePadModal'
import PinEntryModal from '@/components/Globals/Modals/Signing/PinEntryModal'
import { getUserSignature } from '@/helpers/api/signing'
import request from '@/helpers/request'
import { getZippedFiles } from '@/helpers/api/fileUploads'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'CommunicationFolioModal',
  components: { ModalStickyHeader, InteractionEditor, Interactions, AttachmentSourceButton, SeenByUsers, ClosureSignature, SignaturePadModal, PinEntryModal },
  data: () => ({
    finishing: false,
    sendingInteraction: false,
    interactionIdempotencyKey: null,
    interactionMessage: '',
    mentionedUserIds: [],
    replyInteraction: null,
    loadingFilePath: false,
    loadingPdf: false,
    files: [],
    filesPreview: [],
    libraryFiles: [],
    acknowledgementIdempotencyKey: null,
    acknowledgementMenuOpen: false,
    showSignaturePad: false,
    showPinModal: false,
    signatureUploading: false,
    isSigning: false,
    visibilityDialog: false,
    pendingVisibility: null,
    metadataMenuOpen: false,
    searchUserResponsable: '',
    descriptionEditing: false,
    descriptionDraft: '',
  }),
  computed: {
    currentFolio() {
      return this.$store.communicationBooksStore.state.currentFolio
    },
    dialogOpen: {
      get() {
        return !!this.currentFolio
      },
      set(value) {
        if (!value) this.closeModal()
      },
    },
    bookName() {
      const { books, selectedBookId } = this.$store.communicationBooksStore.state
      return books.find(book => book.id === selectedBookId)?.name || '-'
    },
    publisherName() {
      return this.currentFolio?.publisher?.info?.name || this.currentFolio?.publisher?.name || this.currentFolio?.publisher_name || '-'
    },
    attachments() {
      return this.currentFolio?.files || this.currentFolio?.attachments || []
    },
    associations() {
      return this.currentFolio?.associations || []
    },
    canInteract() {
      return this.currentFolio?.status === 'published'
        && typeof this.checkPermissions === 'function'
        && this.checkPermissions(['interact_communication_book_folios'])
    },
    acknowledgements() {
      return this.currentFolio?.acknowledgements || []
    },
    acknowledgedByMe() {
      return !!this.currentFolio?.acknowledged_by_me
    },
    acknowledgementLoading() {
      return this.$store.communicationBooksStore.state.acknowledgementLoading
    },
    acknowledgementError() {
      return this.$store.communicationBooksStore.state.acknowledgementError
    },
    userInfo() {
      return this.$store.userStore?.state?.userInfo || {}
    },
    signatures() {
      return this.currentFolio?.current_signatures || this.currentFolio?.user_signatures || []
    },
    signatureHistory() {
      return this.currentFolio?.signature_history || []
    },
    timelineItems() {
      const interactions = (this.currentFolio?.interactions || []).map(interaction => ({
        ...interaction,
        timeline_type: null,
      }))
      const signatures = (this.currentFolio?.signature_history || []).map(signature => ({
        id: `communication-folio-signature-${signature.id}`,
        created_at: signature.signed_at,
        timeline_type: 'signature',
        signature,
      }))

      return [...interactions, ...signatures].sort((left, right) => {
        const leftTime = new Date(left.created_at || 0).getTime()
        const rightTime = new Date(right.created_at || 0).getTime()
        return leftTime - rightTime
      })
    },
    isUserSigned() {
      const signatures = this.currentFolio?.user_signatures || []
      return signatures.some(signature => Number(signature.user_id) === Number(this.userInfo.id))
    },
    canSign() {
      return this.currentFolio?.status === 'published'
        && !this.isUserSigned
        && Number(this.currentFolio?.responsable) === Number(this.userInfo.id)
        && typeof this.checkPermissions === 'function'
        && this.checkPermissions(['sign_communication_book_folios'])
    },
    canFinish() {
      return !!this.currentFolio?.responsable && this.signatures.some(signature => Number(signature.user_id) === Number(this.currentFolio.responsable))
    },
    finishDisabledReason() {
      if (!this.currentFolio?.responsable) return 'Debe indicar el responsable.'
      if (!this.canFinish) return 'Falta la firma vigente del responsable.'
      return ''
    },
    responsibleName() {
      return this.currentFolio?.responsable_user?.name || this.currentFolio?.responsable_user?.info?.name || 'Sin asignar'
    },
    priorityOptions() {
      return [{ text: 'Inmediata', value: 3 }, { text: 'Media', value: 2 }, { text: 'Estándar', value: 1 }]
    },
    editablePriorityOptions() {
      const currentPriority = Number(this.currentFolio?.priority || 2)
      return this.priorityOptions.filter(option => Number(option.value) !== currentPriority)
    },
    priorityLabel() {
      return this.priorityOptions.find(option => Number(option.value) === Number(this.currentFolio?.priority || 2))?.text || 'Normal'
    },
    priorityClass() {
      return Number(this.currentFolio?.priority) === 3 ? 'pill--priority-high' : Number(this.currentFolio?.priority) === 2 ? 'pill--priority-medium' : 'pill--priority-low'
    },
    filteredResponsibleUsers() {
      const search = this.searchUserResponsable.toLowerCase()
      const currentResponsible = Number(this.currentFolio?.responsable)
      return (this.$store.usersStore.state.usersListForItems?.users || []).filter(user => {
        if (Number(user.id) === currentResponsible) return false
        return !search || (user.name || '').toLowerCase().includes(search) || (user.email || '').toLowerCase().includes(search)
      })
    },
    metadataSaving() {
      return this.$store.communicationBooksStore.state.metadataSaving
    },
    canManageCommunicationBooks() {
      return typeof this.checkPermissions === 'function'
        && this.checkPermissions(['create_communication_book_folios'])
    },
    isOpeningFolio() {
      return Number(this.currentFolio?.number) === 1
    },
    canEditDescription() {
      return this.isOpeningFolio
        && this.currentFolio?.status === 'published'
        && (Number(this.currentFolio?.created_by) === Number(this.userInfo.id)
          || Number(this.currentFolio?.responsable) === Number(this.userInfo.id))
    },
    descriptionSaving() {
      return this.$store.communicationBooksStore.state.descriptionSaving
    },
    descriptionError() {
      return this.$store.communicationBooksStore.state.descriptionError
    },
    visibilityOptions() {
      return [
        { text: 'Restringido', value: 'restricted' },
        { text: 'Público', value: 'public' },
      ]
    },
    visibilitySaving() {
      return this.$store.communicationBooksStore.state.visibilitySaving
    },
    visibilityError() {
      return this.$store.communicationBooksStore.state.visibilityError
    },
  },
  methods: {
    closeModal() {
      this.$store.communicationBooksStore.commit('setCurrentFolio', null)
      this.descriptionEditing = false
    },
    minimizeFolio() {
      const folio = this.currentFolio
      if (!folio || !folio.id) return

      this.$store.globalStore.commit('addTaskToTasksHandler', {
        folio_id: folio.id,
        title: folio.title,
        type: 'Libro de Comunicaciones',
        type_id: 8,
        number: folio.code,
      })
      this.closeModal()
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString('es-CL')
    },
    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('es-CL')
    },
    startDescriptionEdit() {
      if (!this.canEditDescription) return
      this.descriptionDraft = this.currentFolio.description || ''
      this.descriptionEditing = true
      this.$store.communicationBooksStore.commit('setDescriptionError', null)
    },
    cancelDescriptionEdit() {
      if (this.descriptionSaving) return
      this.descriptionEditing = false
      this.descriptionDraft = ''
    },
    async saveDescription() {
      if (!this.canEditDescription || !String(this.descriptionDraft || '').replace(/<[^>]*>/g, '').trim()) return
      try {
        await this.$store.communicationBooksStore.dispatch('updateFolioDescription', {
          folioId: this.currentFolio.id,
          description: this.descriptionDraft,
          idempotencyKey: uuidv4(),
        })
        this.descriptionEditing = false
        this.descriptionDraft = ''
        await this.reloadFolio()
      } catch (error) {
        // The store exposes the error while keeping the editor open for correction.
      }
    },
    signatureName(signature) {
      return signature?.user?.info?.name || signature?.user?.name || 'Usuario'
    },
    async finish() {
      if (!this.canFinish) return
      this.finishing = true
      try {
        await this.$store.communicationBooksStore.dispatch('finishFolio', this.currentFolio.id)
      } finally {
        this.finishing = false
      }
    },
    async updatePriority(priority) {
      if (Number(priority) === Number(this.currentFolio.priority || 2)) return
      try {
        await this.$store.communicationBooksStore.dispatch('updateFolioMetadata', {
          folioId: this.currentFolio.id,
          data: { priority },
        })
        this.metadataMenuOpen = false
      } catch (error) {
        this.metadataMenuOpen = false
      }
    },
    priorityButtonColor(priority) {
      if (Number(priority) === 3) return '#F4516C'
      if (Number(priority) === 2) return 'warning'
      return '#3EBFA3'
    },
    checkUsers() {
      const projectId = this.currentFolio?.project_id
      const usersState = this.$store.usersStore.state.usersListForItems
      if (!projectId || this.$store.usersStore.state.loadingUserListForItems) return
      if (usersState.project_id !== projectId) {
        this.$store.usersStore.dispatch('getUsersListForItems', { project_id: projectId, start: 0, limit: 9999 })
      }
    },
    async updateResponsable(responsable) {
      if (Number(responsable) === Number(this.currentFolio?.responsable)) return
      this.searchUserResponsable = ''
      await this.$store.communicationBooksStore.dispatch('updateFolioMetadata', {
        folioId: this.currentFolio.id,
        data: { responsable },
      })
    },
    requestVisibilityChange(visibility) {
      const currentVisibility = this.currentFolio.visibility || 'restricted'
      if (visibility === currentVisibility) return
      if (visibility === 'public') {
        this.pendingVisibility = visibility
        this.visibilityDialog = true
        return
      }
      this.updateVisibility(visibility)
    },
    cancelVisibilityChange() {
      this.pendingVisibility = null
      this.visibilityDialog = false
    },
    async confirmVisibilityChange() {
      const visibility = this.pendingVisibility
      this.cancelVisibilityChange()
      if (visibility) await this.updateVisibility(visibility)
    },
    async updateVisibility(visibility) {
      try {
        await this.$store.communicationBooksStore.dispatch('updateFolioVisibility', {
          folioId: this.currentFolio.id,
          visibility,
        })
      } catch (error) {
        this.$store.communicationBooksStore.commit('setVisibilityError', error)
      }
    },
    async acknowledge() {
      if (this.acknowledgedByMe || this.acknowledgementLoading) return
      this.acknowledgementIdempotencyKey = this.acknowledgementIdempotencyKey || uuidv4()
      try {
        await this.$store.communicationBooksStore.dispatch('acknowledgeFolio', {
          folioId: this.currentFolio.id,
          idempotencyKey: this.acknowledgementIdempotencyKey,
        })
        this.acknowledgementIdempotencyKey = null
      } catch (error) {
        this.$store.communicationBooksStore.commit('setAcknowledgementError', error)
      }
    },
    async handleSignFolio() {
      if (!this.canSign || this.signatureUploading || this.isSigning) return
      this.isSigning = true
      try {
        await getUserSignature()
        await this.requestFolioPin()
      } catch (error) {
        if (error && error.status === 404) {
          this.showSignaturePad = true
        } else {
          this.$store.globalStore?.dispatch('addMessage', { message: 'Error al iniciar el proceso de firma.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
        }
      } finally {
        this.isSigning = false
      }
    },
    async requestFolioPin() {
      const response = await this.$store.communicationBooksStore.dispatch('requestFolioSignPin', this.currentFolio.id)
      if (response && response.code === 200) {
        this.showPinModal = true
        this.$store.globalStore?.dispatch('addMessage', { message: 'Se ha enviado un PIN a su correo.', title: 'Información', created: new Date(), type: 'success', icon: 'mdi-email', color: '#2D4B95' })
      } else {
        this.$store.globalStore?.dispatch('addMessage', { message: response?.message || 'Error enviando PIN.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
      }
      return response
    },
    async onSignatureSaved(blob) {
      this.showSignaturePad = false
      if (!blob) return
      this.signatureUploading = true
      try {
        const formData = new FormData()
        formData.append('signature', blob, 'signature.png')
        const response = await request({ url: '/user/setSimpleSignature', method: 'post', data: formData })
        if (response && response.code === 200) {
          await this.requestFolioPin()
        } else {
          this.$store.globalStore?.dispatch('addMessage', { message: response?.message || 'Error guardando la firma.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
        }
      } finally {
        this.signatureUploading = false
      }
    },
    async onPinSubmit(pin) {
      this.showPinModal = false
      if (!pin) return
      this.isSigning = true
      try {
        const response = await this.$store.communicationBooksStore.dispatch('signFolioWithPin', {
          folioId: this.currentFolio.id,
          pin,
        })
        if (response && response.code === 200) {
          await this.reloadFolio()
          this.$store.globalStore?.dispatch('addMessage', { message: 'Firma registrada correctamente.', title: 'Éxito', created: new Date(), type: 'success', icon: 'mdi-check', color: '#10B981' })
        }
      } finally {
        this.isSigning = false
      }
    },
    setReplyInteraction(interaction) {
      this.replyInteraction = interaction
    },
    async reloadFolio() {
      await this.$store.communicationBooksStore.dispatch('loadFolio', this.currentFolio.id)
    },
    async downloadAttachments() {
      if (!this.attachments.length) return
      this.loadingFilePath = true
      const files = await getZippedFiles({ files_to_zip: this.attachments })
      if (files.code === 200) window.open(files.fileurl)
      this.loadingFilePath = false
    },
    async downloadPdf() {
      if (this.loadingPdf) return
      this.loadingPdf = true
      try {
        const response = await this.$store.communicationBooksStore.dispatch('getFolioPdf', this.currentFolio.id)
        if (response && response.pdf_url) window.open(response.pdf_url, '_blank')
      } finally {
        this.loadingPdf = false
      }
    },
    async sendInteraction() {
      const message = String(this.interactionMessage || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      if (!message || this.sendingInteraction) return

      const data = new FormData()
      data.append('message', this.interactionMessage)
      data.append('show_in_feed', '1')
      if (this.replyInteraction) data.append('parent_id', this.replyInteraction.id)
      this.mentionedUserIds.forEach(userId => data.append('mentions[]', userId))
      this.libraryFiles.forEach(file => data.append('library_file_ids[]', file.id))
      this.files.forEach(file => data.append('files[]', file))

      this.sendingInteraction = true
      this.interactionIdempotencyKey = this.interactionIdempotencyKey || uuidv4()
      try {
        const response = await this.$store.communicationBooksStore.dispatch('addInteraction', {
          folioId: this.currentFolio.id,
          data,
          idempotencyKey: this.interactionIdempotencyKey,
        })
        if (!response) return
        this.interactionMessage = ''
        this.mentionedUserIds = []
        this.replyInteraction = null
        this.files = []
        this.filesPreview = []
        this.libraryFiles = []
        this.interactionIdempotencyKey = null
      } finally {
        this.sendingInteraction = false
      }
    },
    async onFilesSelected(files) {
      for (const file of files) {
        this.files.push(file)
        this.filesPreview.push({ name: file.name, size: file.size, uuid: `${Date.now()}-${file.name}` })
      }
    },
    onLibraryFileSelected(file) {
      if (!file || !file.id || this.libraryFiles.some(item => Number(item.id) === Number(file.id))) return
      this.libraryFiles.push(file)
      this.filesPreview.push({ id: file.id, name: file.name, source: 'library' })
    },
    removeAttachment(file) {
      const previewIndex = this.filesPreview.indexOf(file)
      if (previewIndex !== -1) this.filesPreview.splice(previewIndex, 1)
      if (file.source === 'library') {
        const libraryIndex = this.libraryFiles.findIndex(item => Number(item.id) === Number(file.id))
        if (libraryIndex !== -1) this.libraryFiles.splice(libraryIndex, 1)
        return
      }
      const fileIndex = this.files.findIndex(item => item.name === file.name && item.size === file.size)
      if (fileIndex !== -1) this.files.splice(fileIndex, 1)
    },
  },
}
</script>

<style scoped>
/* ─── TOKENS ─────────────────────────────────────────── */
.folio-modal-wrapper::v-deep .folio-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}
.modal-card {
  border-radius: 16px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

/* ─── LOADING ─────────────────────────────────────────── */
.loading-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #F4F6FB;
  border-bottom: 1px solid #E0E5F0;
}
.modal-brand { display: flex; align-items: center; gap: 10px; }
.brand-icon-dark {
  width: 30px; height: 30px; border-radius: 10px;
  background: #E8EEFA; border: 1px solid #C8D4F0;
  display: flex; align-items: center; justify-content: center;
}
.brand-label-dark { font-size: 14px; font-weight: 600; color: #1A1D23; }
.close-btn-dark {
  width: 28px; height: 28px; border-radius: 8px; border: none;
  background: #EEF0F5; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.loading-body {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; padding: 60px 20px;
}
.loading-text { color: #6B7280; font-size: 13px; margin: 0; }

/* ─── TOP BAR ────────────────────────────────────────── */
.topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px 16px;
  background: #F4F6FB;
  border-bottom: 1px solid #E0E5F0;
  flex-shrink: 0;
}
.topbar-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
.topbar-icon {
  width: 36px; height: 36px; border-radius: 10px;
  background: #E8EEFA; border: 1px solid #C8D4F0;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.topbar-titles { flex: 1; min-width: 0; }
.topbar-title {
  font-size: 16px; font-weight: 600; color: #1A1D23;
  margin: 0; line-height: 1.4;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.topbar-number { font-weight: 400; color: #2D4B95; font-size: 14px; }
.topbar-right { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
.topbar-action-btn {
  display: flex; align-items: center; justify-content: center;
  height: 24px !important; padding: 5px !important; min-width: 28px !important;
  aspect-ratio: 1 !important; border-radius: 6px; border: none;
  cursor: pointer; transition: opacity 0.15s;
}
.topbar-action-btn--disabled.v-btn--disabled {
  background-color: #CBD5E1 !important;
  color: #475569 !important;
  opacity: 1 !important;
  cursor: not-allowed;
}
.topbar-action-btn--disabled.v-btn--disabled .v-icon {
  color: #475569 !important;
}
.topbar-action-btn.big { height: 30px !important; min-width: 30px !important; }
.metadata-menu { background: white; border-radius: 6px; overflow: hidden; }
.bg-light-blue-text { background-color: #5E85EF; }

/* ─── STATUS STRIP ───────────────────────────────────── */
.status-strip {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 10px 24px;
  background: #FAFBFF; border-bottom: 1px solid #E5E8EF;
  flex-wrap: wrap; flex-shrink: 0;
}
.status-strip-pills { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; flex: 1; }
.status-strip-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; margin-left: auto; }
.pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 500; line-height: 1.5;
}
.pill--blue  { background: #E8EEFA; color: #2D4B95; }
.pill--red   { background: #FEE2E2; color: #B91C1C; }
.pill--green { background: #DCFCE7; color: #15803D; }
.pill--gray  { background: #F3F4F6; color: #4B5563; }
.pill--priority-high   { background: #FEE2E2; color: #991B1B; }
.pill--priority-medium { background: #FEF3C7; color: #B45309; }
.pill--priority-low    { background: #DCFCE7; color: #15803D; }
.task-closed-notice {
  display: flex; align-items: center; gap: 6px;
  color: #1B7A4E; font-size: 12px; font-weight: 500;
}
.signature-history-row {
  display: flex; align-items: flex-start; gap: 8px; padding: 8px 10px;
  border: 1px solid #E5E8EF; border-radius: 7px; margin-bottom: 6px; font-size: 12px;
}
.signature-history-row--invalid { border-color: #FECACA; background: #FFF7F7; color: #7F1D1D; }
.signature-invalid-label { color: #C62828; font-size: 11px; font-weight: 600; }
.acknowledgement-heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.acknowledgement-confirmed { display: flex; align-items: center; gap: 6px; color: #1B7A4E; font-size: 12px; }
.acknowledge-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px;
  border: none; border-radius: 7px; background: #2D4B95; color: #fff;
  font-size: 12px; font-weight: 600; cursor: pointer;
}
.acknowledge-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.acknowledgement-error { margin: 8px 0 0; color: #C62828; font-size: 12px; }
.signature-status-label { display: inline-flex; align-items: center; gap: 4px; color: #1B7A4E; font-size: 12px; font-weight: 600; }
.signature-list { display: flex; flex-direction: column; gap: 6px; }
.signature-row {
  display: flex; align-items: center; gap: 7px; padding: 7px 9px;
  border: 1px solid #E5E8EF; border-radius: 7px; color: #374151; font-size: 12px;
}
.signature-row small { margin-left: auto; color: #9CA3AF; font-size: 10px; }
.signature-empty { margin: 0 0 10px; color: #9CA3AF; font-size: 12px; }
.sign-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px;
  border: none; border-radius: 7px; background: #1B7A4E; color: #fff;
  font-size: 12px; font-weight: 600; cursor: pointer;
}
.sign-btn:disabled { cursor: not-allowed; opacity: 0.6; }
.verification-card {
  display: flex; align-items: center; gap: 12px; padding: 10px;
  border: 1px solid #E0E7FF; border-radius: 8px; background: #F8FAFF;
}
.verification-qr { width: 64px; height: 64px; flex-shrink: 0; border-radius: 4px; background: white; }
.verification-label { margin: 0; color: #6B7280; font-size: 10px; text-transform: uppercase; }
.verification-code { margin: 2px 0; color: #2D4B95; font-family: 'Courier New', monospace; font-size: 12px; font-weight: 700; }
.verification-hint { margin: 0; color: #6B7280; font-size: 11px; line-height: 1.35; }

/* ─── BODY ───────────────────────────────────────────── */
.modal-body { display: flex; flex: 1; overflow: hidden; min-height: 0; }
.panel-detail {
  width: 50%; min-width: 0; display: flex; flex-direction: column;
  overflow: hidden; flex-shrink: 0; border-right: 1px solid #E5E8EF;
}
.panel-scroll { flex: 1; overflow-y: auto; padding: 20px 24px; }
.panel-scroll::-webkit-scrollbar { width: 4px; }
.panel-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }

.panel-interactions {
  display: flex; flex-direction: column; overflow: hidden;
  background: #FAFBFF; flex: 1 1 0; min-width: 0;
}
.interactions-header {
  padding: 14px 18px; border-bottom: 1px solid #E5E8EF; flex-shrink: 0;
}
.interactions-feed { flex: 1; overflow-y: auto; padding: 12px 18px; }
.empty-interactions {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px; color: #9CA3AF; font-size: 13px;
}
.compose-area { padding: 14px 18px; border-top: 1px solid #E5E8EF; flex-shrink: 0; }
.compose-actions { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 4px; }
.compose-secondary { display: flex; align-items: center; gap: 6px; }
.compose-attachments { display: flex; flex-wrap: wrap; gap: 6px; }
.compose-attachment {
  display: inline-flex; align-items: center; gap: 5px; max-width: 220px;
  padding: 4px 8px; border: 1px solid #E5E8EF; border-radius: 7px;
  background: #F9FAFB; font-size: 12px; color: #374151;
}
.compose-attachment > span:not(.attachment-reference-tag) { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attachment-reference-tag {
  display: inline-flex; align-items: center; margin-left: 2px; padding: 1px 5px;
  border-radius: 4px; background: #EEF2FF; color: #2D4B95; font-size: 10px;
  line-height: 14px; white-space: nowrap;
}
.reply-banner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px; margin-bottom: 10px;
  background: #EEF2FC; border-radius: 6px;
}
.icon-action-btn {
  width: 28px; height: 28px; border-radius: 7px;
  background: #EEF2FF; border: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; transition: background 0.12s;
}
.icon-action-btn:hover { background: #DDE5F9; }
.send-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 7px; border: none;
  background: #2D4B95; color: white; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.send-btn:hover { background: #253d7a; }
.send-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ─── META SECTION ───────────────────────────────────── */
.meta-section { margin-bottom: 20px; }
.meta-two-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.meta-left-column, .meta-right-column { display: flex; flex-direction: column; gap: 12px; }
.meta-item { display: flex; flex-direction: column; gap: 2px; }
.meta-label { font-size: 11px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.04em; }
.meta-value { font-size: 13px; color: #1A1D23; font-weight: 500; }
.meta-responsible { display: flex; align-items: center; gap: 6px; }

/* ─── MENUS ───────────────────────────────────────────── */
.responsible-menu { border-radius: 10px !important; overflow: hidden; width: 280px !important; }
.user-avatar-sm { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.search-responsible-wrapper {
  position: relative;
  padding: 8px 12px;
  border-bottom: 1px solid #E5E8EF;
}
.search-responsible-wrapper::v-deep .v-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.search-responsible-input {
  width: 100%;
  border: 1px solid #E5E8EF;
  border-radius: 6px;
  padding: 8px 12px 8px 32px;
  font-size: 12px;
  outline: none;
  font-family: inherit;
}
.search-responsible-input:focus { border-color: #2D4B95; background-color: #F8F9FC; }
.search-responsible-input::placeholder { color: #999; }

/* ─── CONTENT SECTIONS ───────────────────────────────── */
.content-section { margin-bottom: 20px; padding-top: 16px; border-top: 1px solid #F3F4F6; }
.section-title { font-size: 13px; font-weight: 600; color: #374151; margin: 0 0 10px; }
.section-heading-row { display: flex; align-items: center; justify-content: space-between; }
.description-body { font-size: 13px; color: #374151; line-height: 1.6; overflow-wrap: anywhere; }
.description-warning { margin: 8px 0 0; color: #8a5a00; font-size: 12px; }
.description-error { margin: 8px 0 0; color: #c62828; font-size: 12px; }
.description-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; }
::v-deep .description-body p { margin-bottom: 4px; }
.attachments-list { display: flex; flex-wrap: wrap; gap: 8px; }
.attachment-row {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; padding: 8px 10px; border: 1px solid #E5E8EF; border-radius: 8px;
}
.attachment-info { display: flex; align-items: center; gap: 8px; }
.attachment-name { font-size: 13px; color: #374151; }
</style>

